/**
 * Platform Settings Tab Component
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
// import { Textarea } from '../../ui/textarea';
// AlertDialog and Select imports kept for future merge feature
// import { AlertDialog, ... } from '../../ui/alert-dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Menu, Building2,
  LayoutDashboard, TrendingUp, Calendar, ShoppingCart,
  ClipboardList, CheckCircle, Users, UserPlus, History as HistoryIcon,
  Newspaper, Factory, FileText, Upload,
  PackageCheck, ChevronDown, ChevronRight,
  FileQuestion, DollarSign, FileBox, ListOrdered, BarChart3, Truck, Package,
  AlertTriangle, FileCheck, ClipboardCheck, CalendarClock, PieChart,
  MapPin, Leaf, ShieldCheck, CalendarCheck, Star, type LucideIcon,
} from 'lucide-react';
// import { platformSettingsAPI } from '../../../lib/api';
import { coreAPI, PurchasingTabsVisibility, QualityTabsVisibility } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { DASHBOARD_TAB_REGISTRY } from '../../dashboard/types';
import { PlatformSettings, PORTAL_TYPES, type DashboardTabsVisibility } from './types';
// CR-5466: extracted to its own file to keep PlatformTab from growing further
import { RFQTypesSection } from './RFQTypesSection';
import { FilterVisibilityCard } from './FilterVisibilityCard';
import { OrganisationSection } from './OrganisationSection';
import { DashboardTabSitePanel } from './DashboardTabSitePanel';

// Purchasing sub-tab registry for display
const PURCHASING_SUB_TABS = [
  { id: 'rfq', label: 'RFQ', description: 'Request for Quotation management', icon: FileQuestion },
  { id: 'pricing', label: 'Pricebooks', description: 'Contract pricing and price books', icon: DollarSign },
  { id: 'po_headers', label: 'PO Headers', description: 'Purchase order header listing', icon: FileBox },
  { id: 'po_lines', label: 'PO Lines', description: 'Purchase order line items', icon: ListOrdered },
  { id: 'forecast', label: 'Forecast', description: 'Demand forecasting and planning', icon: BarChart3 },
  { id: 'asn', label: 'ASN', description: 'Advanced Shipping Notices', icon: Truck },
  { id: 'deliveries', label: 'Deliveries', description: 'Goods Receipt Notes and delivery tracking', icon: Package },
] as const;

// Quality sub-tab registry for display
const QUALITY_SUB_TABS = [
  { id: 'ncrs', label: 'NCRs', description: 'Non-Conformance Reports', icon: AlertTriangle },
  { id: 'concessions', label: 'Production Permit / Concession', description: 'Production permits and concessions management', icon: FileCheck },
  { id: 'approval_register', label: 'Approval Register', description: 'Track and manage approvals', icon: ClipboardCheck },
] as const;

// CR-8725: the dashboard tabs, shown under the Dashboard menu item (they were
// on their own Platform Admin page, Dashboard Visibility).
const DASHBOARD_TAB_ICONS: Record<string, LucideIcon> = {
  'default': LayoutDashboard, 'group-kpi': BarChart3, 'group-detail-kpi': PieChart,
  'site-kpi': MapPin, 'cost': DollarSign, 'emissions': Leaf, 'compliance': ShieldCheck,
  'future-forecast': CalendarClock, 'date-adherence': CalendarCheck, 'expediting': Truck,
  'performance-ratings': Star, 'po-lines': ListOrdered,
};
const DASHBOARD_SUB_TABS = DASHBOARD_TAB_REGISTRY.map(tab => ({
  ...tab,
  icon: DASHBOARD_TAB_ICONS[tab.id] ?? LayoutDashboard,
}));
// A tab missing from the stored JSON shows as on for customers, except these
// two, and as off for suppliers. Same reading as the old Dashboard Visibility
// page, so nothing changes for a key an instance has never saved.
const CUSTOMER_DASHBOARD_TABS_DEFAULT_OFF = new Set(['scheduled', 'spot-orders']);

function resolveDashboardTabs(stored: DashboardTabsVisibility, fallback: (id: string) => boolean) {
  return Object.fromEntries(
    DASHBOARD_TAB_REGISTRY.map(tab => [tab.id, stored[tab.id] ?? fallback(tab.id)]),
  );
}

interface PlatformTabProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
  purchasingTabs: PurchasingTabsVisibility;
  onPurchasingTabsChange: (tabs: PurchasingTabsVisibility) => void;
  onSavePurchasingTabs: () => void;
  qualityTabs: QualityTabsVisibility;
  onQualityTabsChange: (tabs: QualityTabsVisibility) => void;
  onSaveQualityTabs: () => void;
  dashboardTabs: DashboardTabsVisibility;
  onDashboardTabsChange: (tabs: DashboardTabsVisibility) => void;
  supplierDashboardTabs: DashboardTabsVisibility;
  onSupplierDashboardTabsChange: (tabs: DashboardTabsVisibility) => void;
  /** Saves the customer and supplier dashboard tabs together. */
  onSaveDashboardTabs: () => void;
}

export function PlatformTab({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
  purchasingTabs,
  onPurchasingTabsChange,
  onSavePurchasingTabs,
  qualityTabs,
  onQualityTabsChange,
  onSaveQualityTabs,
  dashboardTabs,
  onDashboardTabsChange,
  supplierDashboardTabs,
  onSupplierDashboardTabsChange,
  onSaveDashboardTabs,
}: PlatformTabProps) {
  // CR-5371: list of customer/supplier Companies used as options for the
  // "Default landing site" dropdown next to the Main Company toggle.
  const [landingSiteOptions, setLandingSiteOptions] = useState<{ id: number; name: string }[]>([]);
  // CR-5371 rework: QA — the site-only dropdown couldn't reproduce the old
  // platform's ability to default a user to the GROUP level. Load company
  // groups alongside sites so the admin can pick either.
  const [landingGroupOptions, setLandingGroupOptions] = useState<{ id: number; name: string }[]>([]);
  const { user } = useAuth();
  const isHelixUser = user?.platform === 'HELIX' || user?.company_type === 'helix';

  // Both lists go through the shared API client (token auth + API base
  // URL). A raw `fetch('/api/v1/...')` resolved against the FRONTEND origin
  // with cookies, came back as the SPA's index.html, and the JSON parse
  // failed — leaving both dropdowns empty with a console warning.
  useEffect(() => {
    let cancelled = false;
    const unwrap = <T,>(data: T[] | { results?: T[] }): T[] =>
      Array.isArray(data) ? data : (data.results ?? []);
    (async () => {
      try {
        const res = await coreAPI.getAccessibleCompanies();
        const list = unwrap<{ id: number; name: string }>(res.data);
        if (!cancelled) {
          setLandingSiteOptions(list.map((c) => ({ id: c.id, name: c.name })));
        }
      } catch (err) {
        // Non-blocking — the dropdown just stays empty if the fetch fails.
        // eslint-disable-next-line no-console
        console.warn('[CR-5371] failed to load landing-site options', err);
      }
    })();
    (async () => {
      try {
        // The group LIST is Helix-only (403 for a tenant admin); a tenant
        // admin can only ever default users to their own group, which the
        // /accessible/ action returns without a permission error.
        let list: { id: number; name: string }[] = [];
        if (isHelixUser) {
          const res = await coreAPI.getCompanyGroups({ is_active: true });
          list = unwrap<{ id: number; name: string }>(res.data);
        } else {
          const res = await coreAPI.getAccessibleCompanyGroup();
          list = res.data ? [res.data] : [];
        }
        if (!cancelled) {
          setLandingGroupOptions(list.map((g) => ({ id: g.id, name: g.name })));
        }
      } catch (err) {
        // Non-blocking — the dropdown just omits groups if the fetch fails.
        // eslint-disable-next-line no-console
        console.warn('[CR-5371] failed to load landing-group options', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isHelixUser]);

  // CR-5371 rework: default_landing_site / default_landing_group are
  // mutually exclusive (enforced server-side too). The dropdown below shows
  // both option sets under one control; selecting one clears the other.
  const landingSelectValue = platformSettings.defaultLandingGroup != null
    ? `group:${platformSettings.defaultLandingGroup}`
    : platformSettings.defaultLandingSite != null
      ? `site:${platformSettings.defaultLandingSite}`
      : '__none__';

  const handleLandingSelectChange = (value: string) => {
    if (value === '__none__') {
      updatePlatform({ defaultLandingSite: null, defaultLandingGroup: null });
      return;
    }
    // Expected shape is `group:<id>` / `site:<id>`. Anything else — an empty
    // or corrupted value — would leave `idStr` undefined, and parseInt would
    // hand NaN straight through to state and on to the API. Ignore instead:
    // silently persisting NaN as a landing target is worse than a no-op.
    const [kind, idStr] = value.split(':');
    const id = Number.parseInt(idStr ?? '', 10);
    if (!Number.isInteger(id)) return;
    if (kind === 'group') {
      updatePlatform({ defaultLandingGroup: id, defaultLandingSite: null });
    } else if (kind === 'site') {
      updatePlatform({ defaultLandingSite: id, defaultLandingGroup: null });
    }
  };

  // Merge Companies state — hidden until feature is production-ready
  // const [companies, setCompanies] = useState<Company[]>([]);
  // const [loadingCompanies, setLoadingCompanies] = useState(false);
  // const [targetCompanyId, setTargetCompanyId] = useState<string>('');
  // const [sourceCompanyId, setSourceCompanyId] = useState<string>('');
  // const [merging, setMerging] = useState(false);
  // const [mergeResult, setMergeResult] = useState<MergeCompaniesResponse | null>(null);

  // State for expanding sub-toggles
  const [purchasingExpanded, setPurchasingExpanded] = useState(false);
  const [qualityExpanded, setQualityExpanded] = useState(false);
  const [dashboardExpanded, setDashboardExpanded] = useState(false);
  const [supplierDashboardExpanded, setSupplierDashboardExpanded] = useState(false);

  const updatePlatform = (updates: Partial<PlatformSettings>) => {
    onPlatformSettingsChange({ ...platformSettings, ...updates });
  };

  const updateMenuVisibility = (key: string, value: boolean) => {
    onPlatformSettingsChange({
      ...platformSettings,
      menuVisibility: { ...platformSettings.menuVisibility, [key]: value }
    });
  };

  const updatePurchasingTab = (key: string, value: boolean) => {
    onPurchasingTabsChange({ ...purchasingTabs, [key]: value });
  };

  const updateQualityTab = (key: string, value: boolean) => {
    onQualityTabsChange({ ...qualityTabs, [key]: value });
  };

  // IP Detection handler — hidden until feature is production-ready

  // Load companies for merge
  // Merge companies handlers — hidden until feature is production-ready
  // const handleLoadCompanies = async () => { ... };
  // const handleMergeCompanies = async () => { ... };

  const customerMenuCount = [
    platformSettings.menuVisibility.dashboard,
    platformSettings.menuVisibility.supplierPerformance,
    platformSettings.menuVisibility.analytics,
    platformSettings.menuVisibility.calendar,
    platformSettings.menuVisibility.partsMaster,
    platformSettings.menuVisibility.partSchedule,
    platformSettings.menuVisibility.purchasing,
    platformSettings.menuVisibility.callOff,
    platformSettings.menuVisibility.quality,
    platformSettings.menuVisibility.suppliers,
    platformSettings.menuVisibility.onboarding !== false,
    platformSettings.menuVisibility.documents,
    platformSettings.menuVisibility.userActivityHistory,
    platformSettings.menuVisibility.questionnaire,
    platformSettings.menuVisibility.newsRelease,
    platformSettings.menuVisibility.dataImport,
  ].filter(v => v).length;

  const supplierMenuItems = [
    platformSettings.menuVisibility.supplierDashboard,
    platformSettings.menuVisibility.supplierQuestionnaires,
    platformSettings.menuVisibility.supplierCalendar,
    platformSettings.menuVisibility.supplierCustomers,
    platformSettings.menuVisibility.supplierDocuments,
    platformSettings.menuVisibility.supplierNewsRelease,
    platformSettings.menuVisibility.supplierSelfPerformance,
    platformSettings.menuVisibility.supplierRfq === true,
    platformSettings.menuVisibility.purchaseOrders,
    platformSettings.menuVisibility.companyProfile,
    platformSettings.menuVisibility.callOff,
  ];
  const supplierMenuCount = supplierMenuItems.filter(v => v !== false).length;
  const supplierMenuTotal = supplierMenuItems.length;

  return (
    <div className="space-y-4">
      {/* Platform Configuration — moved to top per CR-4395 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Menu className="h-5 w-5" />
            Platform Configuration
          </CardTitle>
          <CardDescription>
            Select the portal type and menu structure
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="menuType">Portal Type</Label>
            <select
              id="menuType"
              value={platformSettings.mainMenuType}
              onChange={(e) => updatePlatform({ mainMenuType: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              {PORTAL_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">
              This determines the main menu structure and terminology used throughout the portal
            </p>
          </div>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Platform Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* CR-8227: the customer's organisation, straight after the portal type.
          Helix only — /admin also renders this tab for company admins. */}
      {isHelixUser && (
        <OrganisationSection
          platformSettings={platformSettings}
          onPlatformSettingsChange={onPlatformSettingsChange}
        />
      )}

      {/* Menu Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Menu className="h-5 w-5" />
            Navigation Menu Management
          </CardTitle>
          <CardDescription>
            Control which pages are available. Switching an item off removes it from the side
            navigation and blocks the page itself, including direct URLs, bookmarks and links.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs defaultValue="customer" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="customer">Customer's View</TabsTrigger>
              <TabsTrigger value="supplier">Supplier's View</TabsTrigger>
            </TabsList>

            <TabsContent value="customer" className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b">
                <h3 className="font-semibold text-base">Customer's View Menu Items</h3>
                <Badge variant="secondary">{customerMenuCount} / 16 Enabled</Badge>
              </div>

              <div className="grid gap-3">
                {/* Dashboard with expandable tab toggles (CR-8725) */}
                <ExpandableMenuToggle
                  icon={<LayoutDashboard className="h-4 w-4 text-primary" />}
                  label="Dashboard"
                  description="Main dashboard overview"
                  checked={platformSettings.menuVisibility.dashboard}
                  onChange={(checked) => updateMenuVisibility('dashboard', checked)}
                  expanded={dashboardExpanded}
                  onExpandToggle={() => setDashboardExpanded(!dashboardExpanded)}
                  subTabs={isHelixUser ? DASHBOARD_SUB_TABS : []} /* CR-8238: Helix only */
                  subTabValues={resolveDashboardTabs(dashboardTabs, id => !CUSTOMER_DASHBOARD_TABS_DEFAULT_OFF.has(id))}
                  onSubTabChange={(id, value) => onDashboardTabsChange({ ...dashboardTabs, [id]: value })}
                  onSave={onSaveDashboardTabs}
                  saving={saving}
                  subTabsWhenOff
                />
                {isHelixUser && dashboardExpanded && ( /* CR-9099 */
                  <DashboardTabSitePanel platformTabs={resolveDashboardTabs(dashboardTabs, id => !CUSTOMER_DASHBOARD_TABS_DEFAULT_OFF.has(id))} />
                )}
                <MenuToggleItem icon={<TrendingUp className="h-4 w-4 text-primary" />} label="Supplier Performance" description="View supplier metrics and KPIs" checked={platformSettings.menuVisibility.supplierPerformance} onChange={(checked) => updateMenuVisibility('supplierPerformance', checked)} />
                <MenuToggleItem icon={<PieChart className="h-4 w-4 text-primary" />} label="Analytics" description="Reports, dashboards, and data analytics" checked={platformSettings.menuVisibility.analytics !== false} onChange={(checked) => updateMenuVisibility('analytics', checked)} />
                <MenuToggleItem icon={<Calendar className="h-4 w-4 text-primary" />} label="Calendar" description="Schedule and manage events" checked={platformSettings.menuVisibility.calendar} onChange={(checked) => updateMenuVisibility('calendar', checked)} />
                <MenuToggleItem icon={<Package className="h-4 w-4 text-primary" />} label="Parts Master" description="Manage parts and SKUs" checked={platformSettings.menuVisibility.partsMaster} onChange={(checked) => updateMenuVisibility('partsMaster', checked)} />
                <MenuToggleItem icon={<CalendarClock className="h-4 w-4 text-primary" />} label="Part Schedule" description="Part-level firm-boundary module (isolated)" checked={platformSettings.menuVisibility.partSchedule} onChange={(checked) => updateMenuVisibility('partSchedule', checked)} />
                {/* Purchasing with expandable sub-toggles */}
                <ExpandableMenuToggle
                  icon={<ShoppingCart className="h-4 w-4 text-primary" />}
                  label="Purchasing"
                  description="Manage purchase orders"
                  checked={platformSettings.menuVisibility.purchasing}
                  onChange={(checked) => { updateMenuVisibility('purchasing', checked); if (!checked) setPurchasingExpanded(false); }}
                  expanded={purchasingExpanded}
                  onExpandToggle={() => setPurchasingExpanded(!purchasingExpanded)}
                  subTabs={isHelixUser ? PURCHASING_SUB_TABS : []} /* CR-8238: Helix only */
                  subTabValues={purchasingTabs}
                  onSubTabChange={updatePurchasingTab}
                  onSave={onSavePurchasingTabs}
                  saving={saving}
                />
                <MenuToggleItem icon={<PackageCheck className="h-4 w-4 text-primary" />} label="Call Off" description="Stock planning and call-off management" checked={platformSettings.menuVisibility.callOff} onChange={(checked) => updateMenuVisibility('callOff', checked)} />

                {/* Quality with expandable sub-toggles */}
                <ExpandableMenuToggle
                  icon={<CheckCircle className="h-4 w-4 text-primary" />}
                  label="Quality"
                  description="Quality management and NCRs"
                  checked={platformSettings.menuVisibility.quality}
                  onChange={(checked) => { updateMenuVisibility('quality', checked); if (!checked) setQualityExpanded(false); }}
                  expanded={qualityExpanded}
                  onExpandToggle={() => setQualityExpanded(!qualityExpanded)}
                  subTabs={isHelixUser ? QUALITY_SUB_TABS : []} /* CR-8238: Helix only */
                  subTabValues={qualityTabs}
                  onSubTabChange={updateQualityTab}
                  onSave={onSaveQualityTabs}
                  saving={saving}
                />

                <MenuToggleItem icon={<Users className="h-4 w-4 text-primary" />} label="Suppliers" description="Manage supplier database" checked={platformSettings.menuVisibility.suppliers} onChange={(checked) => updateMenuVisibility('suppliers', checked)} />
                <MenuToggleItem icon={<UserPlus className="h-4 w-4 text-primary" />} label="Supplier Onboarding" description="Onboarding new suppliers, their questionnaires and renewals (compliance). Off removes it for customers and suppliers, and suppliers are added directly instead." checked={platformSettings.menuVisibility.onboarding !== false} onChange={(checked) => updateMenuVisibility('onboarding', checked)} />
                <MenuToggleItem icon={<FileText className="h-4 w-4 text-primary" />} label="Documents" description="Document management" checked={platformSettings.menuVisibility.documents} onChange={(checked) => updateMenuVisibility('documents', checked)} />
                <MenuToggleItem icon={<HistoryIcon className="h-4 w-4 text-primary" />} label="User Activity History" description="Track user actions and logs" checked={platformSettings.menuVisibility.userActivityHistory} onChange={(checked) => updateMenuVisibility('userActivityHistory', checked)} />
                <MenuToggleItem icon={<ClipboardList className="h-4 w-4 text-primary" />} label="Questionnaire" description="Supplier questionnaires" checked={platformSettings.menuVisibility.questionnaire} onChange={(checked) => updateMenuVisibility('questionnaire', checked)} />
                <MenuToggleItem icon={<Newspaper className="h-4 w-4 text-primary" />} label="News Release" description="Latest news and updates" checked={platformSettings.menuVisibility.newsRelease} onChange={(checked) => updateMenuVisibility('newsRelease', checked)} />
                <MenuToggleItem icon={<Upload className="h-4 w-4 text-primary" />} label="Data Import" description="Import data from external systems" checked={platformSettings.menuVisibility.dataImport} onChange={(checked) => updateMenuVisibility('dataImport', checked)} />
              </div>

              <Button onClick={onSave} disabled={saving} className="w-full mt-4">
                {saving ? 'Saving...' : 'Save Menu Visibility Settings'}
              </Button>
            </TabsContent>

            <TabsContent value="supplier" className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b">
                <h3 className="font-semibold text-base">Supplier's View Menu Items</h3>
                <Badge variant="secondary">{supplierMenuCount} / {supplierMenuTotal} Enabled</Badge>
              </div>

              <div className="grid gap-3">
                <ExpandableMenuToggle
                  icon={<LayoutDashboard className="h-4 w-4 text-primary" />}
                  label="Dashboard"
                  description="Supplier-side dashboard. Hide for compliance-only tenants that only need onboarding + questionnaires."
                  checked={platformSettings.menuVisibility.supplierDashboard !== false}
                  onChange={(checked) => updateMenuVisibility('supplierDashboard', checked)}
                  expanded={supplierDashboardExpanded}
                  onExpandToggle={() => setSupplierDashboardExpanded(!supplierDashboardExpanded)}
                  subTabs={isHelixUser ? DASHBOARD_SUB_TABS : []} /* CR-8238: Helix only */
                  subTabValues={resolveDashboardTabs(supplierDashboardTabs, () => false)}
                  onSubTabChange={(id, value) => onSupplierDashboardTabsChange({ ...supplierDashboardTabs, [id]: value })}
                  onSave={onSaveDashboardTabs}
                  saving={saving}
                  subTabsWhenOff
                />
                <MenuToggleItem icon={<ClipboardList className="h-4 w-4 text-primary" />} label="Questionnaires" description="List of qualification questionnaires assigned to the supplier." checked={platformSettings.menuVisibility.supplierQuestionnaires !== false} onChange={(checked) => updateMenuVisibility('supplierQuestionnaires', checked)} />
                <MenuToggleItem icon={<Calendar className="h-4 w-4 text-primary" />} label="Calendar" description="Calendar / events feed. Hide if not in use." checked={platformSettings.menuVisibility.supplierCalendar !== false} onChange={(checked) => updateMenuVisibility('supplierCalendar', checked)} />
                <MenuToggleItem icon={<Users className="h-4 w-4 text-primary" />} label="Customers" description="List of customers the supplier trades with. Hide when the supplier only deals with one buyer." checked={platformSettings.menuVisibility.supplierCustomers !== false} onChange={(checked) => updateMenuVisibility('supplierCustomers', checked)} />
                <MenuToggleItem icon={<FileText className="h-4 w-4 text-primary" />} label="Documents" description="Shared + uploaded document library." checked={platformSettings.menuVisibility.supplierDocuments !== false} onChange={(checked) => updateMenuVisibility('supplierDocuments', checked)} />
                <MenuToggleItem icon={<TrendingUp className="h-4 w-4 text-primary" />} label="Supplier Performance" description="The supplier's own scores, one row per customer it trades with." checked={platformSettings.menuVisibility.supplierSelfPerformance !== false} onChange={(checked) => updateMenuVisibility('supplierSelfPerformance', checked)} />
                <MenuToggleItem icon={<Newspaper className="h-4 w-4 text-primary" />} label="News Release" description="Customer-published news announcements." checked={platformSettings.menuVisibility.supplierNewsRelease !== false} onChange={(checked) => updateMenuVisibility('supplierNewsRelease', checked)} />
                <MenuToggleItem icon={<ClipboardList className="h-4 w-4 text-primary" />} label="RFQs" description="RFQs the supplier has been invited to quote on. Off by default." checked={platformSettings.menuVisibility.supplierRfq === true} onChange={(checked) => updateMenuVisibility('supplierRfq', checked)} />
                <MenuToggleItem icon={<ShoppingCart className="h-4 w-4 text-primary" />} label="Purchase Orders" description="View and manage POs" checked={platformSettings.menuVisibility.purchaseOrders} onChange={(checked) => updateMenuVisibility('purchaseOrders', checked)} />
                <MenuToggleItem icon={<Factory className="h-4 w-4 text-primary" />} label="Company Profile" description="Manage company information" checked={platformSettings.menuVisibility.companyProfile} onChange={(checked) => updateMenuVisibility('companyProfile', checked)} />
                <MenuToggleItem icon={<PackageCheck className="h-4 w-4 text-primary" />} label="Call Off" description="Call-off management and stock upload" checked={platformSettings.menuVisibility.callOff} onChange={(checked) => updateMenuVisibility('callOff', checked)} />
              </div>

              <Button onClick={onSave} disabled={saving} className="w-full mt-4">
                {saving ? 'Saving...' : 'Save Menu Visibility Settings'}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Supplier Default Landing Page */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5" />
            Supplier Default Landing Page
          </CardTitle>
          <CardDescription>
            Where approved supplier users land after login. Pre-approval suppliers are always sent to My Onboarding.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier-landing" className="text-base font-medium">Landing page</Label>
            <Select
              value={platformSettings.supplierDefaultLandingPath}
              onValueChange={(value) => updatePlatform({ supplierDefaultLandingPath: value })}
            >
              <SelectTrigger id="supplier-landing" className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="/dashboard">Dashboard</SelectItem>
                <SelectItem value="/onboarding/my-progress">My Onboarding</SelectItem>
                <SelectItem value="/purchasing">Sales Orders</SelectItem>
                <SelectItem value="/suppliers">Company Profile</SelectItem>
                <SelectItem value="/intelligence/qualifications">Questionnaires</SelectItem>
                <SelectItem value="/customers">Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Landing Page'}
          </Button>
        </CardContent>
      </Card>

      {/* Main Company Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Supplier/Customer Main Company
          </CardTitle>
          <CardDescription>
            Configure main company settings and permissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label className="text-base font-medium">Main Company Can Log In As</Label>
              <p className="text-sm text-muted-foreground">
                Allow main company users to impersonate sub-accounts
              </p>
            </div>
            <Switch
              checked={platformSettings.mainCompanyCanLoginAs}
              onCheckedChange={(checked) => updatePlatform({ mainCompanyCanLoginAs: checked })}
            />
          </div>

          {/* CR-5371: Default Main Site/Group Selection — when set, new users
              and users whose current_site/current_group are both empty land
              on this site or group at login. If unset, existing default
              behaviour is preserved.
              Rework (QA): the old platform also allowed defaulting to the
              GROUP level ("My Company - Group"), not just a specific site —
              both option sets are offered here, mutually exclusive. */}
          <div className="flex items-center justify-between p-4 border rounded-lg gap-4">
            <div className="flex-1 min-w-0">
              <Label className="text-base font-medium">Default Landing Site</Label>
              <p className="text-sm text-muted-foreground">
                Site or group users are directed to after login when no current site/group is set. Leave empty to keep existing behaviour.
              </p>
            </div>
            <div className="w-64">
              <Select
                value={landingSelectValue}
                onValueChange={handleLandingSelectChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No default — use existing behaviour" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default — use existing behaviour</SelectItem>
                  {landingGroupOptions.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Groups</SelectLabel>
                      {landingGroupOptions.map((g) => (
                        <SelectItem key={`group:${g.id}`} value={`group:${g.id}`}>{g.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {landingSiteOptions.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Sites</SelectLabel>
                      {landingSiteOptions.map((c) => (
                        <SelectItem key={`site:${c.id}`} value={`site:${c.id}`}>{c.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* CR-4802: declared filter visibility (where it belongs is CR-8223). */}
      <FilterVisibilityCard />

      {/* Free Mode - Hidden until feature is production-ready */}
      {/* IP Restriction - Hidden until feature is production-ready */}
      {/* Merge Companies - Hidden until feature is production-ready
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Merge Companies
          </CardTitle>
          <CardDescription>
            Merge duplicate company records, transferring all data to the target company
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Target Company (Keep)</Label>
              <Select
                value={targetCompanyId}
                onValueChange={setTargetCompanyId}
                onOpenChange={(open) => { if (open) handleLoadCompanies(); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingCompanies ? 'Loading...' : 'Select target company'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} ({c.company_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This company will receive all data from the source
              </p>
            </div>

            <div className="space-y-2">
              <Label>Source Company (Delete)</Label>
              <Select
                value={sourceCompanyId}
                onValueChange={setSourceCompanyId}
                onOpenChange={(open) => { if (open) handleLoadCompanies(); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingCompanies ? 'Loading...' : 'Select source company'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.filter(c => String(c.id) !== targetCompanyId).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} ({c.company_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-destructive">
                This company will be deleted after merge
              </p>
            </div>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                disabled={!targetCompanyId || !sourceCompanyId || merging}
              >
                {merging ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging...</>
                ) : (
                  'Execute Merge'
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Company Merge</AlertDialogTitle>
                <AlertDialogDescription>
                  This action is irreversible. All data from the source company will be
                  transferred to the target company, and the source company will be permanently deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleMergeCompanies}>
                  Confirm Merge
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {mergeResult && (
            <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <p className="font-semibold text-green-600">{mergeResult.message}</p>
              <p className="text-sm">
                Total records transferred: {mergeResult.total_records_transferred}
              </p>
              {mergeResult.transfers.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">Transfer Details:</p>
                  <div className="text-xs space-y-1">
                    {mergeResult.transfers.map((t, i) => (
                      <div key={i} className="flex justify-between bg-background p-2 rounded">
                        <span>{t.model}.{t.field}</span>
                        <Badge variant="secondary">{t.records_transferred} records</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      */}

      {/* User Notification Defaults moved to its own tab */}

      {/* CR-5888: "Manage Commodity Groups and Types" relocated to the
          client-facing Admin Setup → Master Data → Commodities area so client
          admins can maintain it without Valuechain intervention. */}

      {/* CR-5466: Admin-managed RFQ Type dropdown for Create RFQ wizard */}
      <RFQTypesSection />
    </div>
  );
}

// Helper component for menu toggle items
interface MenuToggleItemProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function MenuToggleItem({ icon, label, description, checked, onChange }: MenuToggleItemProps) {
  return (
    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded">
          {icon}
        </div>
        <div>
          <Label className="text-sm font-medium">{label}</Label>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

// Reusable expandable menu toggle with sub-tabs
interface SubTab {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface ExpandableMenuToggleProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  expanded: boolean;
  onExpandToggle: () => void;
  subTabs: readonly SubTab[];
  subTabValues: Record<string, boolean>;
  onSubTabChange: (id: string, value: boolean) => void;
  onSave: () => void;
  saving: boolean;
  /**
   * Offer the sub tabs while the item itself is off. Dashboard needs this
   * (CR-8725): its Scheduled tab also gates OTIF Exclusions and GRN Repair,
   * so it must stay switchable on an install that hides the Dashboard page.
   */
  subTabsWhenOff?: boolean;
}

function ExpandableMenuToggle({
  icon, label, description, checked, onChange,
  expanded, onExpandToggle, subTabs, subTabValues,
  onSubTabChange, onSave, saving, subTabsWhenOff = false,
}: ExpandableMenuToggleProps) {
  const enabledCount = subTabs.filter(tab => subTabValues[tab.id]).length;
  const showSubTabs = (checked || subTabsWhenOff) && subTabs.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded">{icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">{label}</Label>
              {showSubTabs && (
                <Badge variant="outline" className="text-xs">
                  {enabledCount} / {subTabs.length} tabs
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          {showSubTabs && (
            <button
              type="button"
              onClick={onExpandToggle}
              className="p-1 hover:bg-accent rounded mr-2"
              title={expanded ? 'Collapse sub-tabs' : 'Expand sub-tabs'}
            >
              {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </button>
          )}
        </div>
        <Switch checked={checked} onCheckedChange={onChange} />
      </div>
      {showSubTabs && expanded && (
        <div className="ml-6 pl-4 border-l-2 border-muted space-y-2">
          <p className="text-xs text-muted-foreground mb-2">Control which tabs are visible within {label}</p>
          {subTabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <div key={tab.id} className="flex items-center justify-between p-2 border rounded-lg hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-2">
                  <IconComponent className="h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <Label className="text-xs font-medium">{tab.label}</Label>
                    <p className="text-xs text-muted-foreground">{tab.description}</p>
                  </div>
                </div>
                <Switch checked={subTabValues[tab.id]} onCheckedChange={(v) => onSubTabChange(tab.id, v)} />
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={onSave} disabled={saving} className="w-full mt-2">
            {saving ? 'Saving...' : 'Save Tab Visibility'}
          </Button>
        </div>
      )}
    </div>
  );
}
